import { showError } from '@calendly/alert';
import { useVariant } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconButton } from '@calendly/ui/components/icon-button';
import { SettingsMenuVerticalIcon } from '@calendly/ui/components/icons';
import { Item } from '@calendly/ui/components/menu';
import classNames from 'classnames';
import { useMemo, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEnforceUpgrade } from 'components/billing/useEnforceUpgrade';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { isAvailableFeatureSelector } from 'features/billing';
import { useEventTypeEditorSidebar } from 'features/event_type_editor';
import { usePopupSwitcher } from 'hooks/use_popup_switcher';
import { isAdminOrOwnerSelector, isOnlyGroupAdminSelector, userGroupRelationshipsSelector } from 'selectors/current_user';
import { useDestroyManagedEventTypeMutation, useDuplicateManagedEventTypeMutation } from './api';
import { DestroyPopup } from './destroy_popup';
import { useManagedEventTypeContext } from './managed_event_types_data';
import { UserGroupPopup } from './user_group_popup';
const actionsCss = "a17fr268";
const Actions = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsCss, props.className)} />;
});
export const ActionsMenu = ({
  onEdit
}) => {
  const {
    id,
    name,
    owningUserGroup,
    assignedOrganizationMembershipsCount: membershipsCount,
    assignedUserGroupsCount: groupsCount
  } = useManagedEventTypeContext();
  const [toggle, Dropdown, isExpanded] = useDropdown();
  const dispatch = useDispatch();
  const shouldDelayPaywall = useVariant('met_paywall_delay_experiment') === 'on';
  const {
    openPopup,
    closePopup,
    isPopupOpen
  } = usePopupSwitcher();
  const [destroyManagedEventType, {
    isLoading: isDestroyProgress
  }] = useDestroyManagedEventTypeMutation();
  const [duplicateManagedEventType] = useDuplicateManagedEventTypeMutation();
  const enforceUpgrade = useEnforceUpgrade('managed_events');
  const isManagedEventsFeatureAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey: 'managed_events'
  }));
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const isOnlyGroupAdmin = useSelector(isOnlyGroupAdminSelector);
  const userGroupRelationships = useSelector(userGroupRelationshipsSelector);
  const {
    id: openedEventTypeId,
    closeSidebar: closeEventTypeSidebar,
    isOpen: isETEditorOpen
  } = useEventTypeEditorSidebar();
  const userGroupAdminRelationships = useMemo(() => Object.values(userGroupRelationships).filter(relationship => !!relationship.admin), [userGroupRelationships]);
  const disabled = !isManagedEventsFeatureAvailable && !shouldDelayPaywall;
  const canEdit = isAdminOrOwner || isOnlyGroupAdmin && !!owningUserGroup;
  const canDuplicate = canEdit || isOnlyGroupAdmin && !owningUserGroup;
  const handleApplyDuplicate = ({
    selectedUserGroup
  }) => {
    const {
      group_uuid: userGroupUuid
    } = selectedUserGroup;
    duplicateManagedEventType({
      id,
      userGroupUuid
    });
  };
  const duplicateManagedEventTypeHandler = () => {
    if (isAdminOrOwner) {
      if (owningUserGroup) {
        duplicateManagedEventType({
          id,
          userGroupUuid: owningUserGroup.uuid
        });
        return;
      }
      duplicateManagedEventType({
        id
      });
      return;
    }
    if (isOnlyGroupAdmin && userGroupAdminRelationships.length > 1) {
      openPopup('user_group_select')();
      return;
    }
    duplicateManagedEventType({
      id,
      userGroupUuid: userGroupAdminRelationships[0].admin.group_uuid
    });
  };
  const handleDuplicate = () => isManagedEventsFeatureAvailable ? duplicateManagedEventTypeHandler() : enforceUpgrade();
  const onDestroy = async () => {
    try {
      await destroyManagedEventType({
        id
      });
      if (isETEditorOpen) {
        const sidebarOpenWithSameMET = String(id) === String(openedEventTypeId);
        if (sidebarOpenWithSameMET) {
          closeEventTypeSidebar();
        }
      }
    } catch (e) {
      dispatch(showError(e.error.message));
    }
  };
  return <>
      <Actions>
        <IconButton size="small" aria-expanded={isExpanded} title={I18n.t('publisher.components.table.action_menu_for', {
        item: name
      })} onClick={toggle}>
          <SettingsMenuVerticalIcon />
        </IconButton>
        <Dropdown>
          <ViewportDropdownMenu>
            {canEdit && <Item onClick={onEdit} disabled={disabled}>
                {I18n.t('publisher.action.edit')}
              </Item>}
            {canDuplicate && <Item onClick={handleDuplicate} disabled={disabled}>
                {I18n.t('publisher.organization.managed_event_types.table.actions.duplicate')}
              </Item>}
            {canEdit && <Item onClick={openPopup('destroy_managed_event_type')}>
                {I18n.t('publisher.action.delete')}
              </Item>}
          </ViewportDropdownMenu>
        </Dropdown>
      </Actions>

      <DestroyPopup eventTypeName={name} membershipsCount={membershipsCount} groupsCount={groupsCount} isOpen={isPopupOpen('destroy_managed_event_type')} onClose={closePopup} isDestroyProgress={isDestroyProgress} onDestroy={onDestroy} />

      <UserGroupPopup isOpen={isPopupOpen('user_group_select')} onClose={closePopup} handleApply={handleApplyDuplicate} owningUserGroup={owningUserGroup} showCancelButton />
    </>;
};

require("./actions_menu.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./actions_menu.js");